import { FC, useEffect, useState } from "react";

import { 
  TextField, FormHelperText, Grid,
  Alert,
  ButtonProps
} from "@mui/material";
import { Button } from "@gisce/oficina-virtual-components";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Controller, useFormContext } from "react-hook-form";
import useCoupon, { UseCouponProps } from "./hooks/useCoupon";

interface ICouponFieldProps extends UseCouponProps {
  checkSearchParams?: boolean;
  fieldName?: string;
  multiple?: boolean;
  outterIsSubmitting?: boolean;
  submitButtonLabel: string;
  usedCodes?: string[];
}

const CouponField: FC<ICouponFieldProps> = ({
  association,
  checkSearchParams = false,
  fieldName = "promotionalCode",
  multiple = false,
  outterIsSubmitting = false,
  submitButtonLabel,
  usedCodes = [],
  type,
}) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [textFieldValue, setTextFieldValue] = useState('');
  const [isSubmitting, setIsSubmitting] =  useState(outterIsSubmitting);
  const [couponHasError, setCouponHasError] = useState(false);
  const [searchParamsErrorCoupons, setSearchParamsErrorCoupons] = useState<string[]>([]);

  const {handleValidateCoupon} = useCoupon({association, type});

  const {control, getValues, setValue} = useFormContext(); 

  useEffect(() => {

    const checkPromocodes = async (promocodes: string[]) => {
      let invalidPromocodes: string[] = [];
      for (const code of promocodes) {
        const valid = await handleValidateCoupon(code);
        if (valid) {
          addCorrectCoupon(code);
        } else {
          invalidPromocodes.push(code);
        }
      }
      setSearchParamsErrorCoupons(invalidPromocodes);
    }

    if (checkSearchParams) {

      const promocodeQS = searchParams.get("promocode");
      searchParams.delete("promocode");
      setSearchParams(searchParams);

      let promocodes: string[] = []
      if (promocodeQS?.includes(",")) {
        promocodes = promocodeQS.split(",");
        if (multiple) {
          // Remove duplicates
          promocodes = [...new Set(promocodes)];
        } else {
          promocodes = promocodes.slice(0,1);
        }
      } else if (promocodeQS) {
        promocodes.push(promocodeQS);
      }

      checkPromocodes(promocodes);
    }
    
  }, []);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Inner component
    setTextFieldValue(event.target.value);
    setCouponHasError(false);
  }

  const addCorrectCoupon = (coupon: string) => {
    if (multiple) {
      setValue(fieldName, [...getValues(fieldName), coupon]);
    } else {
      setValue(fieldName, [coupon]);
    }
    setTextFieldValue('');
  }

  const applyChange = () => {
    if(textFieldValue) {

      // Si el cupó ja ha estat activat
      if (
        getValues(fieldName).includes(textFieldValue) || 
        usedCodes.includes(textFieldValue)
      ) {
        setCouponHasError(true);
        return;
      }

      setIsSubmitting(true);
      handleValidateCoupon(textFieldValue)
      .then(isValid => {
        if (isValid) {
          addCorrectCoupon(textFieldValue);
          setCouponHasError(false);
        } else {
          setCouponHasError(true);
        }
      })
      .finally(() => {
        setIsSubmitting(false);
      })
    }
  }

  return (
    <>
      <Grid container spacing={1} 
        sx={{width: {md: "50%", xs: "100%"}, mb: "10px"}}
      >
        <Grid item xs={10}>
          <Controller 
            control={control}
            name={fieldName}
            render={() => 
              <>
                <TextField
                  label={t("common:text.contractation_promotional_code")}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={textFieldValue}
                  onChange={handleChange}
                  disabled={isSubmitting || (!multiple && !!getValues(fieldName).length)}
                  error={!isSubmitting && couponHasError}
                />
      
                {!isSubmitting && couponHasError && 
                  <FormHelperText error={true}>
                    {t("common:text.contractation_promotional_invalid")}
                  </FormHelperText>
                }
              </>
            }
          />
        </Grid>

        <Grid item xs={2}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            loading={isSubmitting}
            disabled={isSubmitting || (!multiple && !!getValues(fieldName).length)}
            onClick={applyChange}
          >
            {submitButtonLabel}
          </Button>
        </Grid>

        {!!searchParamsErrorCoupons.length && <>
          <Grid item xs={10}>
            <Alert 
              variant="outlined"
              severity="error"
              onClose={() => {setSearchParamsErrorCoupons([])}}
            >
              {t("common:text.contractation_promotional_which_invalid", {
                count: searchParamsErrorCoupons.length, 
                promocode: searchParamsErrorCoupons.join(", ")
              })}
            </Alert>
          </Grid>
        </>}
      </Grid>
    </>
  )
};

export default CouponField;